import {
  getIsTravelApp,
  isCorpTenant,
  PATH_CARS,
  travelAppInterface,
  TravelAppInterfaceEventName,
  TravelAppInterfaceFunnelType,
  useUserContext,
  UTM_SOURCE_PARAM,
} from "@capone/common";
import {
  fetchCorporateExperimentsByBusiness,
  resolvePreselectAccountId,
} from "@capone/experiments";
import { fetchUserPolicies } from "b2b-base/src/api/v1/policies/fetchPolicies";
import { usePrevious } from "halifax";
import React, { useEffect, useState } from "react";
import { Route, useHistory, useLocation } from "react-router-dom";
import {
  B2B_PORTAL_AUTH_REDIRECT_TO,
  B2B_PORTAL_UNAUTHORIZED_PATH,
  CallState,
} from "redmond";
import { fetchUserInfo } from "../../../api/v1/fetchUserInfo";
import { fetchActiveExperiments } from "../../../api/v1/experiments/fetchExperiments";
import { preselectBusiness, PreselectPayloadType } from "../../../api/v1/multi-account/preselectBusiness";
import { trackEvent } from "../../../api/v1/trackEvent";
import { useExperiments } from "../../../context/experiments";
import config from "../../../utils/config";
import {
  PATH_ACTIVITIES,
  PATH_FLIGHTS,
  PATH_HOTELS,
  PATH_PACKAGES,
  PATH_PREMIER_COLLECTION,
  PATH_PREMIER_VACATION_RENTALS,
  PATH_STAYS,
  PATH_VACATION_RENTALS,
  PATH_WEBVIEW,
  UNPROTECTED_PATHS,
} from "../../../utils/urlPaths";
import { useMedalliaFeedback } from "../../MedalliaFeedback";

interface IProtectedRouteProps {
  protectedUrl: string;
  protectedContent: (pathname: string) => JSX.Element;
  unprotectedContent: (notAuthenticated: boolean) => JSX.Element;
  onAccountPreselected?: () => void;
  isAccountPreselected?: boolean;
}

// Mapping of utm_source values to card products for preselection
const UTM_TO_CARD_PRODUCT: Record<string, string> = {
  scptest: "SparkCashPlus",
};

const ProtectedRoute = (props: IProtectedRouteProps) => {
  const { protectedUrl, protectedContent, unprotectedContent, onAccountPreselected, isAccountPreselected } = props;
  const [authenticated, setAuthenticated] = useState(false);
  const [displayUnathenticatedPage, setDisplayUnathenticatePage] =
    useState(false);

  const currentLocation = useLocation();

  const prevLocation = usePrevious(currentLocation);

  const expState = useExperiments();

  const { updateSessionInfo, setPolicies } = useUserContext(config.TENANT);
  const history = useHistory();

  const isCorporate = isCorpTenant(config.TENANT);

  useEffect(() => {
    const runPreselection = async () => {
      if (!isCorporate) return;
      if (isAccountPreselected) return;

      const utmSource = new URLSearchParams(window.location.search).get(UTM_SOURCE_PARAM);
      if (!utmSource) return;

      // CARD PRODUCT FLOW: Check mapping first for direct card product preselection
      const cardProduct = UTM_TO_CARD_PRODUCT[utmSource.toLowerCase()];

      if (cardProduct) {
        try {
          await preselectBusiness({
            PreselectPayload: PreselectPayloadType.ByCardProduct,
            cardProduct,
          });
          onAccountPreselected?.();
          trackEvent({
            eventName: "account_preselect_success",
            properties: { source: utmSource },
          });
        } catch (e) {
          trackEvent({
            eventName: "account_preselect_failure",
            properties: {
              source: utmSource,
              error_type: "api_error",
              error_description: e instanceof Error ? e.message : String(e),
            },
          });
        }
        return; // Exit early - card product flow handled this utm_source
      }

      // FDA FALLBACK FLOW: Experiment-gated preselection by account reference ID
      try {
        const [{ experiments }, businessExperiments] = await Promise.all([
          fetchActiveExperiments(),
          fetchCorporateExperimentsByBusiness(),
        ]);
        const gateExperiment = experiments.find(
          (exp) => exp.id === "corp-free-fda-account-preselect"
        );
        const accountId = resolvePreselectAccountId(
          utmSource,
          gateExperiment,
          businessExperiments
        );
        if (!accountId) {
          trackEvent({
            eventName: "account_preselect_failure",
            properties: {
              source: utmSource,
              error_type: "no_eligible_account",
              error_description: gateExperiment
                ? `Gate passed but no single eligible account resolved. Business experiments count: ${businessExperiments.length}`
                : "Gate experiment corp-free-fda-account-preselect not found",
            },
          });
          return;
        }

        await preselectBusiness({ PreselectPayload: PreselectPayloadType.ByAccountReferenceId, accountReferenceId: accountId });
        onAccountPreselected?.();
        trackEvent({
          eventName: "account_preselect_success",
          properties: { source: utmSource },
        });
      } catch (e) {
        trackEvent({
          eventName: "account_preselect_failure",
          properties: {
            source: utmSource,
            error_type: "api_error",
            error_description: e instanceof Error ? e.message : String(e),
          },
        });
      }
    };

    const fetchUser = async () => {
      await runPreselection();

      try {
        const userInfoResponse = await fetchUserInfo<typeof config.TENANT>();
        if (updateSessionInfo) {
          updateSessionInfo(userInfoResponse);
        }
      } catch {
        if (UNPROTECTED_PATHS.includes(window.location.pathname)) {
          setDisplayUnathenticatePage(true);
        } else {
          const redirectUrl = `${window.location.pathname}${window.location.search}`;
          if (!redirectUrl.includes("/auth/")) {
            sessionStorage.setItem(B2B_PORTAL_AUTH_REDIRECT_TO, redirectUrl);
          }
          history.push(B2B_PORTAL_UNAUTHORIZED_PATH);
        }
      }
    };
    if (!authenticated) fetchUser();
  }, [updateSessionInfo, authenticated]);

  useEffect(() => {
    if (
      expState.callState !== CallState.InProcess &&
      expState.callState !== CallState.NotCalled
    ) {
      setAuthenticated(true);
    }
  }, [expState]);

  useEffect(() => {
    const fetchPolicy = async () => {
      if (setPolicies) {
        try {
          const userPolicies = await fetchUserPolicies();
          setPolicies(userPolicies.policyDetail);
        } catch (error) {
          console.error(error);
        }
      }
    };
    if (isCorporate && expState.experiments.length > 0) {
      fetchPolicy();
    }
  }, [setPolicies, isCorporate, expState.experiments]);

  useEffect(() => {
    // intercept homepage routing from travel app
    if (
      prevLocation &&
      currentLocation.pathname !== prevLocation?.pathname &&
      [
        PATH_FLIGHTS,
        PATH_HOTELS,
        PATH_STAYS,
        PATH_CARS,
        PATH_PREMIER_COLLECTION,
        PATH_PACKAGES,
        PATH_ACTIVITIES,
        PATH_VACATION_RENTALS,
        PATH_PREMIER_VACATION_RENTALS,
      ].includes(currentLocation.pathname)
    ) {
      const { isTravelApp } = getIsTravelApp(prevLocation.search);

      if (isTravelApp) {
        history.replace({
          pathname: PATH_WEBVIEW,
          search: "?isTravelApp=true",
        });

        const currentFunnel = (() => {
          switch (currentLocation.pathname) {
            case PATH_FLIGHTS:
              return TravelAppInterfaceFunnelType.FLIGHTS;
            case PATH_HOTELS:
            case PATH_STAYS:
              return TravelAppInterfaceFunnelType.HOTELS;
            case PATH_CARS:
              return TravelAppInterfaceFunnelType.CARS;
            case PATH_PREMIER_COLLECTION:
              return TravelAppInterfaceFunnelType.PREMIUM_STAYS;
            case PATH_PACKAGES:
              return TravelAppInterfaceFunnelType.PACKAGES;
            case PATH_ACTIVITIES:
              return TravelAppInterfaceFunnelType.ACTIVITIES;
            case PATH_VACATION_RENTALS:
            case PATH_PREMIER_VACATION_RENTALS:
              return TravelAppInterfaceFunnelType.HOMES;
            default:
              return TravelAppInterfaceFunnelType.HOTELS;
          }
        })();

        const prevFunnel = (() => {
          switch (true) {
            case prevLocation.pathname.includes(PATH_FLIGHTS):
              return TravelAppInterfaceFunnelType.FLIGHTS;
            case prevLocation.pathname.includes(PATH_HOTELS):
            case prevLocation.pathname.includes(PATH_STAYS):
              return TravelAppInterfaceFunnelType.HOTELS;
            case prevLocation.pathname.includes(PATH_CARS):
              return TravelAppInterfaceFunnelType.CARS;
            case prevLocation.pathname.includes(PATH_VACATION_RENTALS):
              return TravelAppInterfaceFunnelType.HOMES;
            case prevLocation.pathname.includes(PATH_PREMIER_COLLECTION):
              return TravelAppInterfaceFunnelType.PREMIUM_STAYS;
            case prevLocation.pathname.includes(PATH_PACKAGES):
              return TravelAppInterfaceFunnelType.PACKAGES;
            case prevLocation.pathname.includes(PATH_ACTIVITIES):
              return TravelAppInterfaceFunnelType.ACTIVITIES;
            default:
              return undefined;
          }
        })();

        travelAppInterface.sendEvent({
          name: TravelAppInterfaceEventName.NAVIGATE_TO_NATIVE_FEATURE,
          funnel: prevFunnel,
          data: {
            feature: {
              name: "book",
              function: {
                name: "search",
                data: {
                  funnelType: currentFunnel,
                },
              },
            },
          },
        });
      }
    }
  }, [currentLocation]);

  useMedalliaFeedback();

  return (
    <Route
      path={protectedUrl}
      render={(prop) =>
        // eslint-disable-next-line no-nested-ternary
        authenticated
          ? protectedContent(prop.history.location.pathname)
          : displayUnathenticatedPage
          ? unprotectedContent(!authenticated)
          : null
      }
    />
  );
};

export default ProtectedRoute;
